<template>
    <app-layout :title="$t('Storage template presets')">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                {{ $t('Storage template presets') }}
            </h2>
        </template>

        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <div class="flex flex-col">
                <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                        <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead class="bg-gray-50">
                                <tr>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {{ $t('Name') }}
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                                        {{ $t('Create for new users') }}
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                                        {{ $t('Enable for users') }}
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                                        {{ $t('Readonly access for users') }}
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                                        {{ $t('Invoice storage') }}
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                                        {{ $t('Report storage') }}
                                    </th>
                                    <th scope="col" class="relative px-6 py-3">
                                        &nbsp;
                                    </th>
                                </tr>
                                </thead>
                                <tbody class="bg-white divide-y divide-gray-200">
                                <template v-if="presets.length > 0">
                                    <tr v-for="preset in presets">
                                        <td class="px-6 py-4 whitespace-nowrap" :colspan="editedItem == preset.id ? 6 : 0">
                                            <div class="flex items-center">
                                                <div class="text-sm font-medium text-gray-900 w-full"
                                                     v-if="editedItem == preset.id">
                                                    <jet-input type="text" class="mt-1 block w-full"
                                                               v-model="form.name"/>
                                                    <jet-input-error :message="form.errors.name" class="mt-2"/>
                                                </div>
                                                <div class="text-sm font-medium text-gray-900"
                                                     v-else>
                                                    {{ preset.name }}
                                                </div>
                                            </div>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-center" v-if="editedItem != preset.id">
                                            <yes-no-toggle :state="preset.is_included"
                                                           @click.prevent="updateIncludedState(preset)"></yes-no-toggle>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-center" v-if="editedItem != preset.id">
                                            <yes-no-toggle :state="preset.is_active"
                                                           @click.prevent="updateActiveState(preset)"></yes-no-toggle>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-center" v-if="editedItem != preset.id">
                                            <yes-no-toggle :state="preset.is_readonly"
                                                           @click.prevent="updateReadonlyState(preset)"></yes-no-toggle>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-center" v-if="editedItem != preset.id">
                                            <yes-no-toggle :state="preset.is_invoices_container"
                                                           @click.prevent="updateIsInvoicesContainerState(preset)"></yes-no-toggle>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-center" v-if="editedItem != preset.id">
                                            <yes-no-toggle :state="preset.is_reports_container"
                                                           @click.prevent="updateIsReportsContainerState(preset)"></yes-no-toggle>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div class="flex items-center justify-end space-x-3">
                                                <template v-if="!prohibitEditMode">
                                                    <a href="#" class="text-primary-600 hover:text-primary-900"
                                                       @click.prevent="activateItem(preset)">{{ $t('Edit') }}</a>
                                                </template>
                                                <template v-else-if="editedItem == preset.id">
                                                    <a href="#" class="text-success-600 hover:text-success-900"
                                                       @click.prevent="submitForm(preset)">
                                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6"
                                                             fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                  stroke-width="2" d="M5 13l4 4L19 7"/>
                                                        </svg>
                                                    </a>
                                                    <a href="#" class="text-danger-600 hover:text-danger-900"
                                                       @click.prevent="resetItem()">
                                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6"
                                                             fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                  stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                                                        </svg>
                                                    </a>
                                                </template>
                                                <template v-else>

                                                </template>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                                <template v-else>
                                    <tr>
                                        <td colspan="4" class="px-6 py-4 whitespace-nowrap text-center">
                                            {{ $t('No data available.') }}
                                        </td>
                                    </tr>
                                </template>
                                <tr v-if="creationMode">
                                    <td class="px-6 py-4 whitespace-nowrap" colspan="6">
                                        <div class="flex items-center">
                                            <div class="text-sm font-medium text-gray-900 w-full">
                                                <jet-input type="text" class="mt-1 block w-full"
                                                           v-model="form.name" :placeholder="$t('Template name')"/>
                                                <jet-input-error :message="form.errors.name" class="mt-2"/>
                                            </div>
                                        </div>
                                    </td>

                                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div class="flex items-center justify-end space-x-3">
                                            <a href="#" class="text-success-600 hover:text-success-900"
                                               @click.prevent="addPreset()">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6"
                                                     fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                          stroke-width="2" d="M5 13l4 4L19 7"/>
                                                </svg>
                                            </a>
                                            <a href="#" class="text-danger-600 hover:text-danger-900"
                                               @click.prevent="resetCreationMode()">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6"
                                                     fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                          stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                                                </svg>
                                            </a>
                                        </div>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex py-4 justify-end" v-if="!prohibitEditMode">
                <jet-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing"
                            @click.prevent="activateCreationMode()">
                    {{ $t('Add new template') }}
                </jet-button>
            </div>
        </div>
    </app-layout>
</template>

<script>
import {defineComponent} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import JetInput from '@/Jetstream/Input.vue'
import JetButton from '@/Jetstream/Button.vue'
import YesNoToggle from '@/Components/YesNoToggle.vue'
import JetInputError from "@/Jetstream/InputError";

export default defineComponent({
    components: {
        AppLayout,
        YesNoToggle,
        JetInput,
        JetButton,
        JetInputError,
    },

    props: {
        presets: Object,
    },

    computed: {
        prohibitEditMode() {
            return this.editedItem != null || this.creationMode;
        }
    },

    data() {
        return {
            form: this.$inertia.form({
                name: null,
                is_included: null,
                is_active: null,
                is_readonly: null,
                is_invoices_container: null,
                is_reports_container: null,
                sort_order: null,
            }),
            editedItem: null,
            creationMode: false,
        };
    },

    methods: {
        activateItem(preset) {
            this.fillFormWithPreset(preset);
            this.editedItem = preset.id;
        },

        submitForm() {
            this.putForm(this.editedItem);
        },

        resetItem() {
            this.resetForm();
            this.editedItem = null;
        },

        activateCreationMode() {
            this.fillFormWithPreset({
                name: '',
                is_included: 1,
                is_active: 1,
                is_readonly: 0,
                is_invoices_container: 0,
                is_reports_container: 0,
            })
            this.creationMode = true;
        },

        resetCreationMode() {
            this.resetForm();
            this.creationMode = false;
        },

        addPreset() {
            this.form.post(route('container-presets.store'), {
                preserveScroll: true,
                // preserveState: false,
                onSuccess: () => {
                    this.resetForm();
                    this.resetCreationMode();
                },
            });
        },

        updateIncludedState(preset) {
            this.putInvertedBooleanState(preset, 'is_included');
        },

        updateActiveState(preset) {
            this.putInvertedBooleanState(preset, 'is_active');
        },

        updateReadonlyState(preset) {
            this.putInvertedBooleanState(preset, 'is_readonly');
        },

        updateIsInvoicesContainerState(preset) {
            this.putInvertedBooleanState(preset, 'is_invoices_container');
        },

        updateIsReportsContainerState(preset) {
            this.putInvertedBooleanState(preset, 'is_reports_container');
        },

        putInvertedBooleanState(preset, field) {
            this.fillFormWithPreset(preset);
            this.form[field] = !this.form[field];

            this.putForm(preset.id);
        },

        putForm(presetId) {
            this.form.put(route('container-presets.update', {preset: presetId}), {
                preserveScroll: true,
                // preserveState: false,
                onSuccess: () => {
                    this.resetForm();
                    this.resetItem();
                },
            });
        },

        fillFormWithPreset(preset) {
            this.resetForm();
            this.form.name = preset.name;
            this.form.is_included = preset.is_included;
            this.form.is_active = preset.is_active;
            this.form.is_readonly = preset.is_readonly;
            this.form.is_invoices_container = preset.is_invoices_container;
            this.form.is_reports_container = preset.is_reports_container;
            this.form.sort_order = preset.sort_order;
        },

        resetForm() {
            this.form.reset();
            this.form.clearErrors();
        },
    }
})
</script>
